import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useUI } from '@/hooks/ui';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { employeeContractService } from '@/api/domains/employeeContract/employeeContract.service.unified';
import { employeeGet, type EmployeeProfile } from '@/api/domains/employee';
import {
  mapEmployeeContractToForm,
  mapUpdateEmployeeContractFormToTransport,
} from '@/api/domains/employeeContract/mappers/employeeContractEdit.mapper';
import type { ValidatedUpdateEmployeeContractFormData } from '@/api/domains/employeeContract/types/employeeContractEdit.types';
import {
  updateEmployeeContractSchema,
  type UpdateEmployeeContractFormData,
} from '@/schemas/employee/employeeContractEdit.schema';
import {
  useHoldingCompanies,
  useContractStatuses,
  useContractTypes,
  useContractModalities,
  useAreas,
  usePositions,
  useCostCenters,
  useUnions,
} from '@/api/domains/catalog';


/** Backend error code returned when the collaborator has no contract in Active state. */
const NO_ACTIVE_CONTRACT_CODE = 'NOT_FOUND_ACTIVE_EMPLOYEE_CONTRACT';

/**
 * True when the response means "this collaborator has no active contract" rather than a failure.
 * Matches on the error code and falls back to the 404 status, so the empty state still works if the
 * response carries no machine-readable code.
 */
const isNoActiveContractResponse = (response: {
  status?: number;
  error?: { code?: string | null };
}): boolean => response.error?.code === NO_ACTIVE_CONTRACT_CODE || response.status === 404;


interface UseEmployeeActiveContractResult {
  isLoading: boolean;
  updating: boolean;
  /** False when the collaborator has no active contract (backend responded 404). */
  hasActiveContract: boolean;
  employeeProfile: EmployeeProfile | null;
  handleCancel: () => void;
  handleUpdate: () => Promise<void>;
  contractStatusOptions: { value: string; label: string }[];
  contractTypeOptions: { value: string; label: string }[];
  contractModalityOptions: { value: string; label: string }[];
  companyOptions: { value: string; label: string }[];
  areaOptions: { value: string; label: string }[];
  positionOptions: { value: string; label: string }[];
  costCenterOptions: { value: string; label: string }[];
  unionOptions: { value: string; label: string }[];
  form: {
    register: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['register'];
    setValue: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['setValue'];
    watch: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['watch'];
    errors: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['formState']['errors'];
    trigger: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['trigger'];
    setError: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['setError'];
  };
}


export function useEmployeeActiveContract(employeeId?: number): UseEmployeeActiveContractResult {
  const navigate = useNavigate();
  const { showNotification, showApiError } = useUI();
  const { t: translate } = useTranslation();
  const { createSignal } = useAbortController();
  const [loadingContract, setLoadingContract] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [hasActiveContract, setHasActiveContract] = useState(false);
  // Identifier of the resolved active contract, used to save through the existing update endpoint
  const [activeContractId, setActiveContractId] = useState<number | null>(null);
  const [employeeProfile, setEmployeeProfile] = useState<EmployeeProfile | null>(null);
  const { companies: holdingCompanies, loading: loadingCompanies } = useHoldingCompanies();
  const { statuses: contractStatuses, loading: loadingStatuses } = useContractStatuses();
  const { types: contractTypes, loading: loadingTypes } = useContractTypes();
  const { modalities: contractModalities, loading: loadingModalities } = useContractModalities();
  const { areas, loading: loadingAreas } = useAreas();
  const { costCenters, loading: loadingCostCenters } = useCostCenters();
  const { unions, loading: loadingUnions } = useUnions();

  const formMethods = useForm<UpdateEmployeeContractFormData>({
    resolver: zodResolver(updateEmployeeContractSchema),
    mode: 'onBlur',
    defaultValues: {
      contractNumber: 0,
      contractName: '',
      contractStatusId: 1,
      contractTypeId: undefined,
      dateEntry: '',
      contractStartDate: '',
      contractEndDate: null,
      contractModalityId: undefined,
      companyContractId: undefined,
      areaId: undefined,
      positionId: undefined,
      costCenterId: undefined,
      unionCode: '',
    },
  });

  // Watch areaId to reload positions when area changes
  const selectedAreaId = formMethods.watch('areaId');
  const { positions } = usePositions(selectedAreaId);

  // Track if initial area was already set (to distinguish initial load from user changes)
  const initialAreaIdSet = useRef(false);
  // Track if user manually changed area (to trigger auto-select when positions load)
  const userChangedArea = useRef(false);

  useEffect(() => {
    if (!selectedAreaId) {
      return;
    }
    if (!initialAreaIdSet.current) {
      initialAreaIdSet.current = true;
      return;
    }
    userChangedArea.current = true;
  }, [selectedAreaId]);

  useEffect(() => {
    if (userChangedArea.current && positions.length > 0) {
      formMethods.setValue('positionId', positions[0].id, { shouldValidate: false });
      userChangedArea.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);

  const loading =
    loadingContract ||
    loadingProfile ||
    loadingCompanies ||
    loadingStatuses ||
    loadingTypes ||
    loadingModalities ||
    loadingAreas ||
    loadingCostCenters ||
    loadingUnions;

  // Load employee profile (needed for the breadcrumb title)
  useEffect(() => {
    if (!employeeId) {
      setLoadingProfile(false);
      return;
    }

    // try/catch/finally covers every path, so the IIFE can never reject
    void (async () => {
      setLoadingProfile(true);
      try {
        const response = await employeeGet.getProfile(employeeId);
        if (response.success && response.data) {
          setEmployeeProfile(response.data);
        }
      } catch {
        // Silently fail - the profile only feeds the breadcrumb title
      } finally {
        setLoadingProfile(false);
      }
    })();
  }, [employeeId]);

  const loadActiveContract = useCallback(async () => {
    if (!employeeId) {
      setLoadingContract(false);
      return;
    }

    setLoadingContract(true);
    const signal = createSignal();
    const response = await employeeContractService.getActive(employeeId, signal);

    if (response.error?.code === 'ABORTED') return;

    if (!response.success || !response.data) {
      // Only the absence of an active contract is an empty state. Any other failure is a real
      // error and must surface, otherwise a 500 or a 403 would be indistinguishable from a
      // collaborator who simply has no active contract.
      if (!isNoActiveContractResponse(response)) {
        showApiError(response, translate('employee:contracts.messages.loadError'));
      }
      setHasActiveContract(false);
      setActiveContractId(null);
      setLoadingContract(false);
      return;
    }

    setHasActiveContract(true);
    setActiveContractId(response.data.employeeContractId);
    formMethods.reset(mapEmployeeContractToForm(response.data));
    setLoadingContract(false);
  }, [employeeId, createSignal, formMethods, showApiError, translate]);

  useEffect(() => {
    void loadActiveContract();
  }, [loadActiveContract]);

  const navigateToProfile = useCallback(() => {
    if (!employeeId) return;
    void navigate(
      ROUTE_DEFINITIONS.EMPLEADOS_PERFIL.path.replace(':employeeId', String(employeeId)),
    );
  }, [navigate, employeeId]);

  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: navigateToProfile,
    isDirty: formMethods.formState.isDirty,
  });

  const handleSaveContract = useCallback(
    async (contractData: ValidatedUpdateEmployeeContractFormData) => {
      if (!employeeId || !activeContractId) return;

      setUpdating(true);
      try {
        const transportData = mapUpdateEmployeeContractFormToTransport(contractData);
        // Dedicated active-contract endpoint: same update service behind it, but authorized by the
        // "Condiciones laborales" section instead of the Contracts interface
        const response = await employeeContractService.updateActive(
          employeeId,
          activeContractId,
          transportData,
        );

        if (response.success && response.data) {
          showNotification('success', translate('employee:contracts.messages.updated'), 5000);
          // Stay on the screen and refresh so the form reflects what was persisted
          confirmNavigation();
          await loadActiveContract();
          return;
        }

        showApiError(response, translate('employee:contracts.messages.updateError'));

        if (response.error?.validationErrors) {
          injectValidationErrors(response.error.validationErrors, formMethods);
        }
      } finally {
        setUpdating(false);
      }
    },
    [
      employeeId, activeContractId, showNotification, showApiError, translate,
      formMethods, confirmNavigation, loadActiveContract,
    ],
  );

  const handleUpdate = useCallback(async () => {
    const isValid = await formMethods.trigger();
    if (!isValid) {
      showNotification('error', translate('common:validation.formErrors'), 5000);
      return;
    }

    const formData = formMethods.getValues();
    await handleSaveContract({
      ...formData,
      employeeId: Number(employeeId),
      contractTypeId: formData.contractTypeId!,
      contractEndDate: formData.contractEndDate ?? null,
      contractModalityId: formData.contractModalityId!,
      companyContractId: formData.companyContractId!,
      areaId: formData.areaId!,
      positionId: formData.positionId!,
      costCenterId: formData.costCenterId!,
    });
  }, [formMethods, employeeId, handleSaveContract, showNotification, translate]);

  return {
    isLoading: loading,
    updating,
    hasActiveContract,
    employeeProfile,
    handleCancel,
    handleUpdate,
    contractStatusOptions: contractStatuses.map((status) => ({
      value: String(status.id),
      label: status.name,
    })),
    contractTypeOptions: contractTypes.map((type) => ({
      value: String(type.id),
      label: type.name,
    })),
    contractModalityOptions: contractModalities.map((modality) => ({
      value: String(modality.id),
      label: modality.name,
    })),
    companyOptions: holdingCompanies.map((company) => ({
      value: String(company.id),
      label: company.name,
    })),
    areaOptions: areas.map((area) => ({
      value: String(area.id),
      label: area.name,
    })),
    positionOptions: positions.map((position) => ({
      value: String(position.id),
      label: position.name,
    })),
    costCenterOptions: costCenters.map((costCenter) => ({
      value: String(costCenter.id),
      label: costCenter.name,
    })),
    unionOptions: unions.map((union) => ({
      value: union.id,
      label: union.name,
    })),
    form: {
      register: formMethods.register,
      setValue: formMethods.setValue,
      watch: formMethods.watch,
      errors: formMethods.formState.errors,
      trigger: formMethods.trigger,
      setError: formMethods.setError,
    },
  };
}

export default useEmployeeActiveContract;
